/* ============================================
   NeonShrine Theme — GSAP Scroll Animations
   ============================================ */

(function () {
  'use strict';

  /* Guard: bail if reduced motion is preferred (OS setting or theme setting) */
  const prefersReducedMotion =
    window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
    document.documentElement.hasAttribute('data-reduce-motion');

  /* Safety net: make reveal-hidden content visible without animating.
     Used when GSAP fails to load (CDN blocked/offline) so content is
     never permanently invisible. */
  function revealEverything() {
    document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale').forEach(el => {
      el.classList.add('is-visible');
    });
  }

  function initAnimations() {
    if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      revealEverything();
      window.__nsAnimReady = true;
      return;
    }

    gsap.registerPlugin(ScrollTrigger);
    /* Mobile browsers fire a window resize when the URL bar collapses on
       scroll; ScrollTrigger's default response is a full refresh() (reverts
       and re-applies every tween + synchronous reflow), which reads as the
       page jumping/resizing right as you start scrolling. Rotation still
       refreshes (it changes the width). */
    ScrollTrigger.config({ ignoreMobileResize: true });
    window.__nsAnimReady = true;

    /* Like "top N%" but clamped to the page's scrollable bounds, so reveals
       near the bottom of short pages (which can never reach the trigger
       line) still fire at max scroll instead of staying invisible. */
    const clampedStart = (viewportPct) => (self) => {
      const absTop = self.trigger.getBoundingClientRect().top + window.scrollY;
      const raw = absTop - window.innerHeight * viewportPct;
      /* Clamp a couple of pixels short of max scroll: the trigger only fires
         once the scroll position passes `start`, and it can never exceed
         maxScroll itself. */
      return Math.max(0, Math.min(raw, ScrollTrigger.maxScroll(window) - 2));
    };

    if (prefersReducedMotion) {
      /* Still make elements visible, just skip animation */
      revealEverything();
      return;
    }

    /* --- Section reveals ---
       Elements already inside the viewport on load are animated right away
       as one top-to-bottom staggered sweep (instead of every ScrollTrigger
       firing simultaneously, which made pages pop in awkwardly). Elements
       below the fold keep their individual scroll triggers. */
    const REVEAL_VARIANTS = [
      ['.reveal', { y: 30 }],
      ['.reveal-left', { x: -30 }],
      ['.reveal-right', { x: 30 }],
      ['.reveal-scale', { scale: 0.9 }]
    ];

    const inViewAtLoad = [];

    REVEAL_VARIANTS.forEach(([selector, fromVars]) => {
      gsap.utils.toArray(selector).forEach(el => {
        const rect = el.getBoundingClientRect();
        const inView = rect.top < window.innerHeight * 0.88 && rect.bottom > 0;

        if (inView) {
          inViewAtLoad.push({ el, fromVars, top: rect.top });
        } else {
          gsap.fromTo(el,
            { opacity: 0, ...fromVars },
            {
              opacity: 1, x: 0, y: 0, scale: 1,
              duration: 0.6,
              ease: 'expo.out',
              scrollTrigger: {
                trigger: el,
                start: clampedStart(0.85),
                toggleActions: 'play none none none'
              }
            }
          );
        }
      });
    });

    inViewAtLoad
      .sort((a, b) => a.top - b.top)
      .forEach((item, i) => {
        gsap.fromTo(item.el,
          { opacity: 0, ...item.fromVars },
          {
            opacity: 1, x: 0, y: 0, scale: 1,
            duration: 0.55,
            ease: 'expo.out',
            delay: 0.08 + Math.min(i * 0.07, 0.45)
          }
        );
      });

    /* --- Staggered grid children --- */
    gsap.utils.toArray('.stagger-reveal').forEach(container => {
      const children = container.children;
      const rect = container.getBoundingClientRect();
      const inView = rect.top < window.innerHeight * 0.88 && rect.bottom > 0;

      const to = {
        opacity: 1, y: 0,
        duration: 0.5,
        ease: 'expo.out',
        stagger: 0.08
      };

      if (inView) {
        to.delay = 0.15;
      } else {
        to.scrollTrigger = {
          trigger: container,
          start: clampedStart(0.85),
          toggleActions: 'play none none none'
        };
      }

      gsap.fromTo(children, { opacity: 0, y: 30 }, to);
    });

    /* --- Hero parallax --- */
    const heroSection = document.querySelector('.hero[data-parallax-hero]');
    if (heroSection) {
      const heroBg = heroSection.querySelector('.hero__bg');
      const heroContent = heroSection.querySelector('.hero__content');

      if (heroBg) {
        gsap.to(heroBg, {
          yPercent: 20,
          ease: 'none',
          scrollTrigger: {
            trigger: heroSection,
            start: 'top top',
            end: 'bottom top',
            scrub: true
          }
        });
      }

      if (heroContent) {
        gsap.to(heroContent, {
          yPercent: -10,
          ease: 'none',
          scrollTrigger: {
            trigger: heroSection,
            start: 'top top',
            end: 'bottom top',
            scrub: true
          }
        });
      }
    }

    /* --- Floating decorative elements --- */
    gsap.utils.toArray('.animate-float-gsap').forEach((el, i) => {
      gsap.to(el, {
        y: -20,
        duration: 3 + i * 0.5,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1
      });
    });

    /* --- Counter animation --- */
    gsap.utils.toArray('[data-count-to]').forEach(el => {
      const target = parseInt(el.getAttribute('data-count-to'));
      const suffix = el.getAttribute('data-count-suffix') || '';

      ScrollTrigger.create({
        trigger: el,
        start: clampedStart(0.85),
        onEnter: () => {
          gsap.to({ val: 0 }, {
            val: target,
            duration: 1.5,
            ease: 'expo.out',
            onUpdate: function () {
              el.textContent = Math.round(this.targets()[0].val) + suffix;
            }
          });
        },
        once: true
      });
    });

    /* --- Section header line expand --- */
    gsap.utils.toArray('.section-header__label').forEach(el => {
      gsap.fromTo(el,
        { opacity: 0 },
        {
          opacity: 1,
          duration: 0.6,
          ease: 'expo.out',
          scrollTrigger: {
            trigger: el,
            start: clampedStart(0.9),
            toggleActions: 'play none none none'
          }
        }
      );
    });
  }

  /* Wait for GSAP to load (deferred script) */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      /* Small delay to ensure GSAP scripts are parsed */
      requestAnimationFrame(initAnimations);
    });
  } else {
    requestAnimationFrame(initAnimations);
  }

  /* Re-init on Shopify section events (theme editor) */
  document.addEventListener('shopify:section:load', () => {
    ScrollTrigger.refresh();
    initAnimations();
  });
})();
